import React, { useEffect, useRef, useState } from "react";
import { useWindowSize } from "@shared/hooks/useWindowSize";

type Option = { label: string; value: string } | string;

type ContextSelectorProps = {
  options: Option[];
  value?: string; // controlled value (lo que se ve en el trigger)
  defaultLabel?: string;
  onSelect?: (value: string) => void;
  disabled?: boolean;
  className?: string;
  /** Meta (lado derecho) */
  rightLabel?: string; // ej. "Customer view"
  rightId?: string; // ej. "ID: D10-CD34FF"
  onRightClick?: () => void; // si queremos que sea clickeable
  rightAriaLabel?: string;
  size?: "sm" | "md" | "lg";
  menuMaxHeightClass?: string;
  isPartnerView?: boolean;
};

const sizeMap = {
  sm: "h-8 text-sm",
  md: "h-9 text-sm",
  lg: "h-12 text-base",
};

const padMap = {
  sm: "px-2",
  md: "px-3",
  lg: "px-4",
};

const ContextSelector: React.FC<ContextSelectorProps> = ({
  options,
  value,
  defaultLabel = "Select",
  onSelect,
  disabled,
  className = "",
  rightLabel,
  rightId,
  onRightClick,
  rightAriaLabel,
  size = "md",
  menuMaxHeightClass = "max-h-72",
  isPartnerView = true,
}) => {
  // Mobile detection - only changes when crossing the 768px breakpoint
  const { width } = useWindowSize();
  const [isMobile, setIsMobile] = useState(() => width <= 768);
  const prevIsMobileRef = useRef(isMobile);

  useEffect(() => {
    const newIsMobile = width <= 768;
    if (prevIsMobileRef.current !== newIsMobile) {
      prevIsMobileRef.current = newIsMobile;
      setIsMobile(newIsMobile);
    }
  }, [width]);

  const [open, setOpen] = useState(false);
  const [internal, setInternal] = useState<string>(value ?? defaultLabel);
  const [anchorRect, setAnchorRect] = useState({ top: 0, left: 0, width: 0 });
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value !== undefined) setInternal(value);
  }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const lbl = (opt: Option) => (typeof opt === "string" ? opt : opt.label);
  const val = (opt: Option) => (typeof opt === "string" ? opt : opt.value);

  const handleOpen = () => {
    if (disabled) return;
    setOpen((prev) => {
      const next = !prev;
      if (next && rootRef.current) {
        const r = rootRef.current.getBoundingClientRect();
        // The menu uses `position: fixed`, which is viewport-relative —
        // do NOT add window.scrollY/X here, that would shift the menu by
        // the scroll amount and either misalign it from the trigger or
        // push it offscreen entirely on a scrolled page.
        setAnchorRect({
          top: r.bottom,
          left: r.left,
          width: r.width,
        });
      }
      return next;
    });
  };

  const handleSelect = (opt: Option) => {
    const v = val(opt);
    if (value === undefined) setInternal(lbl(opt));
    onSelect?.(v);
    setOpen(false);
  };

  const buttonBase =
    "inline-flex items-center justify-between gap-2 transition-colors text-left";

  return (
    <div ref={rootRef} className={`relative w-full ${className}`}>
      {/* Trigger compuesto */}
      <div
        className={[
          `flex w-full items-stretch border border-semantic-border-secondary bg-semantic-bg-primary-alt shadow-sm ${
            isMobile && (rightLabel || rightId)
              ? "rounded-t-sm rounded-b-none"
              : "rounded-sm"
          }`,
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "hover:border-semantic-border-primary",
          sizeMap[size],
        ].join(" ")}
      >
        {/* IZQUIERDA: botón que abre el menú*/}
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          className={`
          ${buttonBase} flex-1 ${padMap[size]} min-w-0 overflow-hidden
          text-semantic-text-secondary
          border-r border-semantic-border-secondary
          ${isMobile ? "" : "border-r border-semantic-border-secondary"}
          bg-semantic-bg-primary-alt
          rounded-l-sm
          rounded-r-sm
          ${
            isMobile && (rightLabel || rightId)
              ? "rounded-tl-sm rounded-tr-sm rounded-bl-none rounded-br-none"
              : isMobile
              ? "rounded-sm"
              : "rounded-l-sm"
          }
          hover:bg-semantic-state-active
          transition-colors
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
        `}
          style={{
            borderTop: "none",
            borderBottom: "none",
            //
            borderLeft: "none",
            borderRight: isMobile ? "none" : undefined,
          }}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide text-left">
            <span className="whitespace-nowrap inline-block">
              {internal || defaultLabel}
            </span>
          </div>
          <svg
            className="h-4 w-4 flex-shrink-0 ml-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 12a.997.997 0 01-.707-.293l-4-4a1 1 0 111.414-1.414L10 9.586l3.293-3.293a1 1 0 011.414 1.414l-4 4A.997.997 0 0110 12z"
              clipRule="evenodd"
            />
          </svg>
        </button>

        {/* DERECHA: pestaña informativa */}
        {!isMobile && (
          <div
            className={`
            flex items-center gap-2 ${padMap[size]}
            border-semantic-border-secondary
            bg-semantic-bg-brand-primary
            text-semantic-text-secondary
            rounded-r-sm
            ${
              isPartnerView
                ? "bg-semantic-bg-brand-primary text-semantic-text-brand"
                : "bg-[rgba(240,240,240,0.8)] text-semantic-text-secondary"
            }
            ${
              onRightClick
                ? "cursor-pointer hover:opacity-90"
                : "cursor-default"
            }

            
        `}
            onClick={onRightClick}
            aria-label={rightAriaLabel}
          >
            {rightLabel && (
              <span
                className={`truncate ${
                  isPartnerView
                    ? "text-semantic-text-brand font-semibold"
                    : "text-semantic-text-secondary"
                }`}
              >
                {rightLabel}
              </span>
            )}
            {rightId && (
              <span
                className={`font-semibold ${
                  isPartnerView
                    ? "text-semantic-text-primary"
                    : "text-semantic-text-primary"
                }`}
              >
                {rightId}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Labels debajo del selector - solo en mobile*/}
      {isMobile && (rightLabel || rightId) && (
        <div
          className={`
            w-full rounded-b-sm border-l border-r border-b border-semantic-border-secondary
            bg-semantic-bg-brand-primary shadow-sm
            ${sizeMap[size]}
            flex items-center ${padMap[size]}
            ${
              isPartnerView
                ? "bg-semantic-bg-brand-primary"
                : "bg-[rgba(240,240,240,0.8)]"
            }
          `}
          style={{
            marginTop: 0,
            borderTop: "none",
          }}
        >
          <div className="flex items-center gap-2 w-full">
            {rightLabel && (
              <span
                className={`text-xs ${
                  isPartnerView
                    ? "text-semantic-text-brand font-semibold"
                    : "text-semantic-text-secondary"
                }`}
              >
                {rightLabel}
              </span>
            )}
            {rightId && (
              <span
                className={`text-xs font-semibold ${
                  isPartnerView
                    ? "text-semantic-text-primary"
                    : "text-semantic-text-primary"
                }`}
              >
                {rightId}
              </span>
            )}
          </div>
        </div>
      )}

      {/* MENÚ */}
      {open && (
        <div
          className={[
            // z-50 keeps the menu above sticky filter bars and tab toggles
            // on the alerts/history pages, which would otherwise render on
            // top of items in the middle of the list (z-20 was not enough).
            "fixed z-50 mt-1 rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt shadow-md",
            menuMaxHeightClass,
            "overflow-y-auto",
          ].join(" ")}
          style={{
            top: anchorRect.top,
            left: anchorRect.left,
            width: anchorRect.width,
          }}
          role="listbox"
          tabIndex={-1}
        >
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-semantic-text-secondary">
              No options available
            </div>
          ) : (
            <ul className="py-1">
              {options.map((opt) => (
                <li key={val(opt)}>
                  <button
                    className="w-full px-3 py-2 text-left text-sm text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-brand"
                    onClick={() => handleSelect(opt)}
                    role="option"
                  >
                    {lbl(opt)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default ContextSelector;
